import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useGlobalLabels } from '@sentecacommerce-theme/lib'
import { Input, Loader } from '../..'
import { ScrollView } from '../ScrollView'
import { IconCheck, IconClose } from '../Icons'

export const Autosuggest = ({
  options,
  updateOptions,
  onOptionSelect,
  isFetching,
  getValue,
  getLabel,
  minLength = 3,
  debounceDelay = 400,
  selectedOption = '',
  disabled = false,
  withRemoveIconWhenSelected = false,
  onRemove,
  hideCheck = false,
  placeholder,
}: AutosuggestProps) => {
  const [query, setQuery] = useState('')
  const [isLoading, setIsLoading] = useState(isFetching)
  const [isFocused, setIsFocused] = useState(false)
  const input = useRef<HTMLInputElement | null>(null)
  const { globalValidations, errorMessages } = useGlobalLabels()

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFocused(true)
    setQuery(e.target.value)
  }

  const hideInput = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleClick = (value: any) => (e: React.MouseEvent<any>) => {
    e.preventDefault()
    e.stopPropagation()

    input.current?.blur()

    onOptionSelect(value)

    setTimeout(() => {
      setQuery('')
    }, 600)

    hideInput()
  }

  const onFocus = () => {
    window.removeEventListener('click', hideInput)
    window.addEventListener('click', hideInput)

    setIsFocused(true)
  }

  useEffect(() => {
    return () => {
      window.removeEventListener('click', hideInput)
    }
  }, [hideInput])

  useEffect(() => {
    if (query.length < minLength) return

    setIsLoading(true)

    if (!debounceDelay) {
      updateOptions(query)
      setIsLoading(false)
      return
    }

    const timeout = setTimeout(() => {
      updateOptions(query)
      setIsLoading(false)
    }, debounceDelay)

    return () => {
      clearTimeout(timeout)
    }
  }, [query, minLength, updateOptions, debounceDelay])

  const isValidQuery = query.length >= minLength

  const isExpandable =
    !selectedOption || (selectedOption && !withRemoveIconWhenSelected)

  const showLoader = (isLoading || isFetching) && isValidQuery

  const getContainerHeight = () => {
    if (!isFocused || !isExpandable) {
      return 0
    }

    if (isValidQuery && !showLoader && options.length) {
      return Math.min(options.length * 40.5, 405)
    }

    return 80
  }

  const height = `${getContainerHeight() / 10}rem`

  const onBlur = () => {
    setTimeout(() => {
      hideInput()
    }, 50)
  }

  const handleRemove = (e?: any) => {
    e?.preventDefault()

    setQuery('')
    onRemove()
  }

  return (
    <Rel>
      <Container
        isDisabled={disabled}
        isFocused={isFocused}
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          onChange={onChange}
          value={selectedOption || query}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          ref={input}
          maxLength={30}
        />

        <OptionList isFocused={isFocused} style={{ height }}>
          {isValidQuery && !showLoader && (
            <ScrollView maxHeight="40.5rem">
              <>
                {options.map((option) => {
                  const value = getValue(option)
                  const label = getLabel(option)

                  return (
                    <Option
                      onMouseDown={handleClick(option)}
                      key={`Option__${value}--${label}`}
                    >
                      {label}
                    </Option>
                  )
                })}
              </>
            </ScrollView>
          )}

          {isValidQuery && !showLoader && !options.length && (
            <Center>{errorMessages?.noResultsFound}</Center>
          )}

          {showLoader && (
            <Center>
              <Loader />
            </Center>
          )}

          {!isValidQuery && (
            <Center>{`${globalValidations?.enterNsymbolsSentence?.begin} ${minLength} ${globalValidations?.enterNsymbolsSentence?.end}`}</Center>
          )}
        </OptionList>
      </Container>
      {!isExpandable && (
        <RemoveIconContainer onClick={handleRemove}>
          <IconClose />
          {!hideCheck && <IconCheck />}
        </RemoveIconContainer>
      )}
    </Rel>
  )
}

export type AutosuggestProps = {
  options: any[]
  getValue: (option: any) => any
  getLabel: (option: any) => string
  onOptionSelect: (value: string | null) => void
  updateOptions: (value: string) => void
  minLength?: number
  debounceDelay?: number
  isFetching: boolean
  selectedOption?: string
  disabled?: boolean
  withRemoveIconWhenSelected?: boolean
  onRemove?: any
  hideCheck?: boolean
  placeholder?: string
}

type StyledProps = {
  isFocused?: boolean
  isDisabled?: boolean
}

const Rel = styled.div`
  position: relative;
`

const DefaultStyle = styled.li.attrs({
  className: 'py-3 px-4',
})`
  display: flex;
  text-align: left;
  border-bottom: 0.1rem solid ${({ theme }) => theme.palette.disabled};
  &:last-child {
    border-bottom: 0;
  }
`

const Option = styled(DefaultStyle)`
  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.disabled};
  }
`

const Center = styled(DefaultStyle)`
  align-items: center;
  justify-content: center;
  height: 100%;
`

const RemoveIconContainer = styled.button.attrs({
  type: 'button',
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex !important;
  border-radius: 0.5rem !important;
  align-items: center;
  justify-content: flex-end;
  padding-right: 2rem;
  z-index: 1000;

  &:hover {
    opacity: 1 !important;
    background-color: rgba(34, 34, 34, 0.1);

    & svg:first-child {
      display: block;
    }

    & svg:last-child {
      display: none;
    }
  }

  & svg:last-child {
    width: 1.5rem;
    height: 1.5rem;
    fill: ${({ theme }) => theme.palette.silverTree};
  }
  & svg:first-child {
    display: none;
    width: 1.3rem;
    height: 1.3rem;
    fill: ${({ theme }) => theme.palette.danger};
  }
`

const Container = styled.div<StyledProps>`
  position: relative;
  ${(props) =>
    props.isDisabled &&
    css`
      &:after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 12;
        border-radius: 0.4rem;
        background: rgba(34, 34, 34, 0.1);
      }
    `}
  ${(props) =>
    props.isFocused &&
    css`
      & input {
        border-color: ${({ theme }) => theme.palette.hint} !important;
      }
    `}
`

const OptionList = styled.ul<StyledProps>`
  height: 0;
  transition: height 0.2s ease;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.secondary};
  position: absolute;
  top: 4.1rem;
  width: 100%;
  border: 0;
  border: 0.1rem solid ${({ theme }) => theme.palette.hint};
  border-top: 0;
  border-bottom-left-radius: 0.4rem;
  border-bottom-right-radius: 0.4rem;
  z-index: -100;
  ${(props) =>
    props.isFocused &&
    css`
      z-index: 100;
    `}
`
